import React from 'react';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import CpsLoader from '../../common/CpsLoader';
import { LoadingGridPanel } from '../../grid/LoadingGridPanel';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Editor, EditorUtils } from '@progress/kendo-react-editor';
import TopBar from '../../topBar/TopBar';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { useGlobalizationContext, useTranslation, useUiAdminConfigsContext } from '@cs-cms/state-management';
import {
  CpsMaintenancePageState,
  CPS_UI_ADMIN_CONFIG_TYPE,
  CpsConfirmDialogState,
  CPS_CONFIRM_DIALOG_STATE
} from '@cs-cms/types';
import { FormRichTextEditorLazy } from '../../form/fields/FormRichTextEditorLazy';
import { FormSwitcher } from '../../form/fields/FormSwitcher';
import { maxLengthRichTextEditor } from '../../form/validators/FormValidator';
import { Footer } from '../../shared/Footer';

interface MaintenancePageDialogState {
  visible: boolean;
  values?: Record<string, any>;
}

export const MaintenancePageManagement: React.FC = () => {
  const [initialValues, setInitValues] = React.useState<Partial<CpsMaintenancePageState>>();
  const [dialogState, setDialogState] = React.useState<MaintenancePageDialogState>({ visible: false });
  const isInitialized = React.useRef(false);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const {
    isLoading,
    uiAdminConfigState,
    upsertUiAdminConfigEntry,
    uiAdminConfigPreviewState,
    setUiAdminConfigPreviewState,
    refreshUiAdminConfigState
  } = useUiAdminConfigsContext();

  const editor = React.useRef<Editor>(null);

  React.useEffect(() => {
    if (isLoading || isInitialized.current) {
      return;
    }

    if (uiAdminConfigState.maintenancePage) {
      const values: CpsMaintenancePageState = uiAdminConfigState.maintenancePage as CpsMaintenancePageState;
      if (editor.current) {
        const view = editor.current.view;
        if (view && values.pageContent) {
          EditorUtils.setHtml(view, values.pageContent);
        }
      }
      setInitValues(values);
    } else {
      setInitValues({});
    }

    isInitialized.current = true;
  }, [isLoading, uiAdminConfigState.maintenancePage]);

  const upsertMaintenancePage = (values: Record<string, any>) => {
    const body = JSON.stringify(values);
    upsertUiAdminConfigEntry(CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE, body);

    if (setUiAdminConfigPreviewState && uiAdminConfigPreviewState?.maintenancePage) {
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined
      });
    }

    refreshUiAdminConfigState(CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE);
  };

  const handleSubmit = (values: Record<string, any>) => {
    if (values.enabled) {
      setDialogState({ visible: true, values });
    } else {
      upsertMaintenancePage(values);
    }
  };

  const handlePreview = (formRenderProps: FormRenderProps) => {
    if (setUiAdminConfigPreviewState) {
      const maintenancePagePreviewState: CpsMaintenancePageState = {
        enabled: true,
        pageContent: formRenderProps.valueGetter('pageContent')
      };
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: maintenancePagePreviewState
      });
    }
  };

  const discardFormChanges = () => {
    if (setUiAdminConfigPreviewState) {
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined
      });
    }
  };

  const handleDialogConfirm = (confirmed: CpsConfirmDialogState) => {
    if (confirmed === CPS_CONFIRM_DIALOG_STATE.YES && dialogState.values) {
      upsertMaintenancePage(dialogState.values);
    }
    setDialogState({ visible: false });
  };

  if (!initialValues) {
    return <CpsLoader />;
  }

  const maxLengthMessage = translate('custom.common.validation.editorContentExceeded');

  return (
    <div>
      {!initialValues && <LoadingGridPanel />}
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => {
          return (
            <FormElement>
              <div>
                <Field name={'enabled'} component={FormSwitcher} label={translate('custom.common.enable')} />
                <h3>{translate('custom.settings.adminCenter.maintenancePage.pageContent')}</h3>
                <Field
                  name={'pageContent'}
                  component={FormRichTextEditorLazy}
                  validator={(value: string) => maxLengthRichTextEditor(value, maxLengthMessage)}
                />
                <div className={'cpsFormButtons'}>
                  <Button
                    type={'button'}
                    themeColor={'secondary'}
                    fillMode="solid"
                    onClick={() => handlePreview(formRenderProps)}
                  >
                    {translate('custom.common.preview')}
                  </Button>
                  <Button themeColor="dark" disabled={!formRenderProps.modified || !formRenderProps.valid}>
                    {translate('custom.common.save')}
                  </Button>
                </div>
              </div>
            </FormElement>
          );
        }}
      />
      {(uiAdminConfigPreviewState.maintenancePage as CpsMaintenancePageState)?.enabled && (
        <Dialog
          onClose={discardFormChanges}
          title={`${translate('custom.settings.adminCenter.maintenancePage.title')} - ${translate('custom.common.preview')}`}
          closeIcon={true}
          width={'100%'}
          height={'100%'}
          contentStyle={{ padding: 0 }}
        >
          <div className="app previewMaintanancePage" style={{}}>
            <TopBar maintainancePagePreview={true} />
            <div
              dangerouslySetInnerHTML={{
                __html: (uiAdminConfigPreviewState.maintenancePage as CpsMaintenancePageState)?.pageContent || ''
              }}
            />
            <Footer />
          </div>
        </Dialog>
      )}
      {dialogState.visible && (
        <ConfirmDialog
          message={translate('custom.settings.adminCenter.maintenancePage.confirmationMessage')}
          onConfirm={handleDialogConfirm}
        />
      )}
    </div>
  );
};
